import { Injectable } from '@angular/core';
import { UrlSerializer, UrlTree, DefaultUrlSerializer, UrlSegmentGroup, Router, NavigationExtras, Params } from '@angular/router';
import { cloneDeep } from 'lodash';
import { isDate } from 'moment';
import { HttpParamsBuilder } from './corrected-http-client';
import { HttpParameterCodecExtended } from './http-parameter-codec-extended';

function isMatrixParams(command) {
  return typeof command === 'object' && command != null && !command.outlets && !command.segmentPath;
}

export function correctRouterParamsSerializer(router: Router, encoder: HttpParameterCodecExtended) {

  const originalCreateUrlTree = router.createUrlTree;

  router.createUrlTree = function (commands: any[], navigationExtras: NavigationExtras = {}): UrlTree {
    if (commands) {
      commands = commands.map((command) => {
        if (isDate(command) || !isMatrixParams(command)) {
          return encoder.valueToString(command);
        }
        else if (isMatrixParams(command)) {
          Object.keys(command)
            .forEach(key => {
              const val = command[key];
              if (Array.isArray(val))
                command[key] = val.map(v => encoder.valueToString(v));
              else
                command[key] = encoder.valueToString(val);
            });

          return command;
        }
      });
    }

    if (navigationExtras.queryParams) {
      const newQueryParams: Params = {};
      Object.keys(navigationExtras.queryParams)
        .forEach(key => {
          const val = navigationExtras.queryParams[key];
          if (Array.isArray(val))
            newQueryParams[key] = val.map(v => encoder.valueToString(v));
          else
            newQueryParams[key] = encoder.valueToString(val);
        });
      navigationExtras.queryParams = newQueryParams;
    }

    return originalCreateUrlTree.apply(router, [commands, navigationExtras]);
  }
}

@Injectable()
export class CorrectedUrlSerializer implements UrlSerializer {

  protected defaultSerializer = new DefaultUrlSerializer();
  constructor(protected encoder: HttpParameterCodecExtended,
    protected paramsBuilder: HttpParamsBuilder) {
  }

  parse(url: any): UrlTree {
    const tree = this.defaultSerializer.parse(unescape(url));

    tree.queryParams = this.encoder.valueFromString(JSON.stringify(tree.queryParams));

    this.parseParams(tree.root);

    return tree;
  }

  serialize(tree: UrlTree): any {
    const localTree = cloneDeep(tree);
    this.serializeParams(localTree.root);

    Object.keys(localTree.queryParams)
      .forEach(key => {
        const val = localTree.queryParams[key];
        if (Array.isArray(val))
          localTree.queryParams[key] = val.map(v => this.encoder.valueToString(v));
        else
          localTree.queryParams[key] = this.encoder.valueToString(val);
      });

    return this.defaultSerializer.serialize(localTree);
  }

  protected serializeParams(segmentGroup: UrlSegmentGroup) {
    segmentGroup.segments.forEach(seg =>
      Object.keys(seg.parameters)
        .forEach(key =>
          seg[key] = this.encoder.valueToString(seg[key])
        )
    )
    Object.values(segmentGroup.children)
      .forEach(child => this.serializeParams(child));
  }

  protected parseParams(segmentGroup: UrlSegmentGroup) {
    segmentGroup.segments.forEach(seg =>
      Object.keys(seg.parameters)
        .forEach(key =>
          seg[key] = this.encoder.valueFromString(seg[key])
        )
    )
    Object.values(segmentGroup.children)
      .forEach(child => this.parseParams(child));
  }
}
